<template>
  <div class="days-schedule">
    <div class="col-12">
      <!-- Title -->
      <div v-if="showTitle" class="section-header-enhanced">
        <v-icon class="section-icon">mdi-calendar-clock</v-icon>
        <h3 class="section-title-text">{{ title || $t("labels.days") }}</h3>
      </div>
      <div
        v-for="(item, index) in value"
        :key="index"
        class="d-flex flex-wrap w-100 day-card"
      >
        <ValidationProvider
          v-slot="{ errors }"
          :name="$t('labels.day')"
          class="col-lg-12"
          rules="required"
        >
          <v-row class="day-header">
            <v-col class="day-selector">
              <v-radio-group
                :value="item.day_of_week"
                @change="updateDay(index, $event)"
                row
                class="day-options"
              >
                <v-radio
                  v-for="day in dayOptions"
                  :key="day.value"
                  :value="day.value"
                  class="day-option"
                >
                  <template #label>
                    <span style="color: var(--main)">{{ day.label }}</span>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>

            <v-col cols="auto" class="day-delete mt-2">
              <v-btn
                v-if="value.length > minDays"
                icon
                :aria-label="$t('delete')"
                @click="removeDay(index)"
                class="row-icon row-icon-delete mt-2"
              >
                <v-icon
                  v-if="value.length > minDays"
                  size="25"
                  :style="{ color: 'red', cursor: 'pointer' }"
                >
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <span v-if="errors[0]" class="error--text d-inline-block">
            {{ errors[0] }}
          </span>
        </ValidationProvider>
        <div
          class="slot-row"
          v-for="(slot, slotIndex) in item.slots"
          :key="slotIndex"
        >
          <!--  -->
          <ValidationProvider
            v-slot="{ errors }"
            :name="$t('labels.start_at_time')"
            class="col-md-5"
            rules="required"
          >
            <base-picker-input
              :name="$t('labels.start_at_time')"
              type="time"
              :value="slot.start_time"
              @input="updateSlot(index, slotIndex, 'start_time', $event)"
              :placeholder="$t('labels.start_at_time')"
            />

            <span v-if="errors[0]" class="error--text d-inline-block">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            :name="$t('labels.end_at_time')"
            class="col-md-5"
            :rules="endTimeRules(slot.start_time)"
          >
            <!-- :rules="`required|end_time_after_start:${slot.start_time}|difference_in_period:${slot.start_time},${period}`" -->
            <base-picker-input
              :name="$t('labels.end_at_time')"
              type="time"
              :value="slot.end_time"
              @input="updateSlot(index, slotIndex, 'end_time', $event)"
              :placeholder="$t('labels.end_at_time')"
            />

            <span v-if="errors[0]" class="error--text d-inline-block">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <v-btn
            icon
            :disabled="item.slots.length <= minSlots"
            :aria-label="$t('delete')"
            @click="removeDaySlot(index, slotIndex)"
            class="row-icon row-icon-delete"
          >
            <v-icon size="25" :style="{ color: 'red', cursor: 'pointer' }">
              mdi-close-circle
            </v-icon>
          </v-btn>
          <v-btn
            v-if="
              item.slots.length == slotIndex + 1 && item.slots.length < maxSlots
            "
            icon
            :aria-label="$t('add')"
            @click="addDaySlot(index)"
            class="row-icon row-icon-add"
          >
            <v-icon
              v-ripple="false"
              size="25"
              :style="{ color: 'var(--main)', cursor: 'pointer' }"
            >
              mdi-plus-circle
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="button_wrapper add-date-btn">
          <v-btn
            block
            :disabled="!canAddDay"
            @click="addNewDate"
            class="add-new-date-btn"
          >
            <v-icon small class="add-date-icon">mdi-plus</v-icon>
            {{ addDayLabel || $t("add_new_date") }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
// v-model: [{ id?, day_of_week: "Saturday", slots: [{ start_time, end_time }] }]
export default {
  name: "DaysSchedule",
  props: {
    // Enable only in forms that require a maximum slot duration.
    validatePeriod: { type: Boolean, default: false },
    period: { type: Number, default: 30 },
    value: { type: Array, default: () => [] },
    days: { type: Array, default: () => [] },
    title: { type: String, default: "" },
    addDayLabel: { type: String, default: "" },
    showTitle: { type: Boolean, default: true },
    minDays: { type: Number, default: 1 },
    maxDays: { type: Number, default: Infinity },
    minSlots: { type: Number, default: 1 },
    maxSlots: { type: Number, default: Infinity },
  },
  computed: {
    dayOptions() {
      if (this.days.length) return this.days;
      const names = [
        "Saturday",
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ];
      return names.map((value) => ({
        value,
        label: this.$t(`days.${value}`),
      }));
    },
    canAddDay() {
      return (
        this.value.length < this.maxDays &&
        this.value.every(
          (day) =>
            day.day_of_week &&
            day.slots.length &&
            day.slots.every(
              (slot) =>
                slot.start_time &&
                slot.end_time &&
                slot.end_time > slot.start_time
            )
        )
      );
    },
  },
  methods: {
    endTimeRules(startTime) {
      const rules = {
        required: true,
        end_time_after_start: { startTime },
      };
      if (this.validatePeriod) {
        rules.difference_in_period = { startTime, period: this.period };
      }
      return rules;
    },
    emitValue(value) {
      this.$emit("input", value);
      this.$emit("change", value);
    },

    updateDay(index, day) {
      this.emitValue(
        this.value.map((item, i) =>
          i === index ? { ...item, day_of_week: day } : item
        )
      );
    },
    updateSlot(index, slotIndex, field, value) {
      this.changeSlots(index, (slots) =>
        slots.map((slot, j) =>
          j === slotIndex ? { ...slot, [field]: value } : slot
        )
      );
    },
    changeSlots(index, update) {
      this.emitValue(
        this.value.map((item, i) =>
          i === index ? { ...item, slots: update(item.slots) } : item
        )
      );
    },
    addDaySlot(index) {
      if (this.value[index].slots.length < this.maxSlots)
        this.changeSlots(index, (slots) => [
          ...slots,
          { start_time: "", end_time: "" },
        ]);
    },
    removeDaySlot(index, slotIndex) {
      if (this.value[index].slots.length > this.minSlots)
        this.changeSlots(index, (slots) =>
          slots.filter((slot, j) => j !== slotIndex)
        );
    },
    removeDay(index) {
      if (this.value.length > this.minDays)
        this.emitValue(this.value.filter((item, i) => i !== index));
    },
    addNewDate() {
      if (this.canAddDay)
        this.emitValue([
          ...this.value,
          {
            day_of_week: "",
            slots: Array.from({ length: Math.max(1, this.minSlots) }, () => ({
              start_time: "",
              end_time: "",
            })),
          },
        ]);
    },
  },
};
</script>
<style lang="scss" scoped>
.days-schedule {
  .section-header-enhanced {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 8px 0 16px;
    .section-icon {
      color: var(--main);
      font-size: 24px;
    }
    .section-title-text {
      margin: 0;
      color: var(--main);
      font-size: 18px;
      font-weight: 700;
    }
  }
  .day-card {
    margin-bottom: 16px;
    padding: 8px 12px 16px;
    border: 1px solid var(--days-border, #e4e8ed);
    border-radius: 16px;
    background: var(--days-background, #f7f9fb);
  }
  .day-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 52px;
    align-items: start;
  }

  .day-delete {
    grid-column: 2;
    grid-row: 1;
    padding-inline: 6px;
  }

  .day-selector {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
  }
  ::v-deep .day-options .v-input--radio-group__input {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 4px;
    max-width: 100%;
    padding-block: 4px;
  }
  @media (max-width: 767px) {
    ::v-deep .day-options .v-input--radio-group__input {
      grid-template-columns: repeat(auto-fit, minmax(85px, 1fr));
    }
  }
  ::v-deep .day-options .v-input__slot {
    min-width: 0;
  }
  ::v-deep .day-option {
    flex: 0 0 auto;
    white-space: nowrap;
    margin: 0 !important;
    padding: 8px 4px;
    border: 1px solid var(--days-border, #e4e8ed);
    border-radius: 10px;
    background: #fff;
  }
  ::v-deep .day-option.v-item--active {
    border-color: var(--main);
  }
  .slot-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 40px 40px;
    gap: 12px;
    align-items: start;
    width: 100%;
    > .col-md-5 {
      width: 100%;
      max-width: none;
      min-width: 0;
    }
    > .row-icon {
      margin-top: 26px;
    }
  }
  .row-icon {
    width: 40px !important;
    min-width: 40px !important;
    height: 40px !important;
    border: 1px solid var(--days-border, #e4e8ed);
    border-radius: 10px;
    background: #fff;
    &:focus-visible {
      outline: 2px solid var(--main);
      outline-offset: 3px;
    }
    &.v-btn--disabled .v-icon {
      color: #adb5bd !important;
    }
  }
  .add-date-btn {
    margin: 4px 0 20px;
  }
  .add-new-date-btn {
    border-radius: 10px;
  }
  .add-date-icon {
    margin-inline-end: 8px;
  }

  width: 100%;
  min-width: 0;
  .slot-row {
    grid-template-columns: repeat(2, minmax(0, 1fr)) auto auto;
  }
  @media (max-width: 575px) {
    .day-card {
      padding: 4px 0 12px;
    }
    .slot-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      padding: 0 8px;
    }
    .slot-row > .col-md-5 {
      grid-column: 1 / -1;
      padding: 0;
    }
    .slot-row > .row-icon {
      margin: 0;
      justify-self: end;
    }
  }
}
</style>
